import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { MaskLine, ease } from "@/components/site/Reveal";
import { BigBlade } from "@/components/site/Logo";
import { scrollToId } from "@/components/site/SmoothScroll";
import { CountUp } from "@/components/site/CountUp";

const STATS = [
  [72, 0, "V", "Rated voltage"],
  [10.7, 1, "HP", "Instant torque"],
  [11.6, 1, "IN", "Max cut depth"],
  [0, 0, "", "Emissions"],
];

function HeroStage() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const bladeRot = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative aspect-square w-full max-w-[680px] mx-auto" style={{ perspective: 1200 }}>
      <motion.div style={{ rotate: bladeRot }} className="absolute inset-0">
        <BigBlade className="h-full w-full animate-spin-slow" />
      </motion.div>
      <div className="absolute inset-[12%] rounded-full bg-[radial-gradient(circle,rgba(212,230,10,0.22),transparent_65%)] blur-2xl" />
      <motion.div style={{ rotateX: rx, rotateY: ry, y, transformStyle: "preserve-3d" }} className="absolute inset-[6%]">
        <motion.img
          data-testid="hero-product-image"
          src="/img/ring-saw.webp"
          alt="BAMF Battery Ring Saw BRS2503-16"
          initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.6, ease, delay: 0.5 }}
          className="h-full w-full object-contain drop-shadow-[0_40px_60px_rgba(0,0,0,0.8)]"
          style={{ transform: "translateZ(60px)" }}
        />
      </motion.div>
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.6, duration: 0.8 }} className="absolute right-0 top-[14%] border border-white/15 bg-black/70 px-3 py-2 font-mono text-[11px] uppercase tracking-widest backdrop-blur-md">
        <span className="text-volt">●</span> BRS2503-16 / Ring Saw
      </motion.div>
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.8, duration: 0.8 }} className="absolute bottom-[12%] left-0 border border-white/15 bg-black/70 px-3 py-2 font-mono text-[11px] uppercase tracking-widest backdrop-blur-md">
        Depth <span className="text-volt">11.6 in</span> / 8 HP
      </motion.div>
    </div>
  );
}

export function Hero() {
  const spot = useRef(null);
  const onMove = (e) => {
    if (!spot.current) return;
    const r = e.currentTarget.getBoundingClientRect();
    spot.current.style.background = `radial-gradient(600px circle at ${e.clientX - r.left}px ${e.clientY - r.top}px, rgba(212,230,10,0.08), transparent 60%)`;
  };

  return (
    <section data-testid="hero-section" onMouseMove={onMove} className="grain relative min-h-[100svh] overflow-hidden bg-obsidian pt-36 md:pt-44">
      <div className="grid-lines absolute inset-0" />
      <div ref={spot} className="pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-[1500px] items-center gap-10 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-6 font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-zinc-300">
            <span className="text-rescue animate-blink">■</span> Battery Activated Multi Force <span className="text-zinc-500">//</span> Grand Rapids, MI
          </motion.p>
          <h1 data-testid="hero-title" className="font-display font-black uppercase leading-[0.86] tracking-tight text-[17vw] sm:text-[13vw] lg:text-[8.6vw] 2xl:text-[9.5rem]" style={{ fontWeight: 900 }}>
            <MaskLine delay={0.1}>Unleash</MaskLine>
            <MaskLine delay={0.22} className="text-volt">The Power.</MaskLine>
            <MaskLine delay={0.34}><span className="text-outline">Redefine</span></MaskLine>
            <MaskLine delay={0.46}>The Rescue<span className="text-rescue">.</span></MaskLine>
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease }} className="mt-8 flex flex-col gap-8 md:flex-row md:items-end">
            <p className="max-w-md text-base md:text-lg leading-relaxed text-zinc-300">
              Next-generation battery rescue saws for fire &amp; emergency response. Zero emissions. Instant torque. Uncompromised performance.
            </p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="hero-shop-button" onClick={() => scrollToId("arsenal")} className="btn-volt h-14 px-7 font-mono text-sm font-bold uppercase tracking-widest">
                <span className="flex items-center gap-3">Shop the Arsenal <ArrowRight className="h-4 w-4" /></span>
              </button>
              <button data-testid="hero-quote-button" onClick={() => scrollToId("contact")} className="h-14 border border-white/25 px-7 font-mono text-sm uppercase tracking-widest transition-colors hover:border-[var(--rescue)] hover:text-rescue">
                Request a Quote
              </button>
            </div>
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <HeroStage />
        </div>
      </div>
      <div className="relative mx-auto mt-10 max-w-[1500px] px-5 md:px-10 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/10">
          {STATS.map(([n, d, u, l], i) => (
            <motion.div key={l} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 + i * 0.1, duration: 0.8, ease }} className="border-b md:border-b-0 border-white/10 py-5 pr-4 md:border-r last:border-r-0">
              <p className="font-display text-4xl md:text-5xl font-extrabold" data-testid={`hero-stat-${i}`}><CountUp value={n} decimals={d} delay={1.2 + i * 0.1} /><span className="text-volt text-2xl ml-1">{u}</span></p>
              <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">{l}</p>
            </motion.div>
          ))}
        </div>
        <button onClick={() => scrollToId("arsenal")} data-testid="hero-scroll-cue" className="mt-6 hidden md:flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400 hover:text-volt">
          <ArrowDownRight className="h-4 w-4" /> Scroll to gear up
        </button>
      </div>
    </section>
  );
}
